import React, { useState, useEffect } from 'react';
import { memberApi } from '../api';
import { friendlyMemberError } from '../api';

// Custom inline SVG icons for premium clinical aesthetic (No emojis)
const SvgLocationPin = () => (
  <svg className="w-4 h-4 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SvgCompass = () => (
  <svg className="w-4 h-4 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const SvgShieldCheck = () => (
  <svg className="w-4 h-4 text-[#2f6f3e]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const SvgBookmark = ({ active = false }) => (
  <svg className={`w-4 h-4 ${active ? 'fill-[#c9a227] text-[#c9a227]' : 'text-[#6d6a63]'}`} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5-7 3.5V5z" />
  </svg>
);

const SvgClose = () => (
  <svg className="w-4 h-4 text-[#6d6a63]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SvgSearch = () => (
  <svg className="w-4 h-4 text-[#6d6a63]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const SvgBriefcase = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export default function TrialsPage() {
  // Profiles and Matches States
  const [profile, setProfile] = useState({
    cancer_type: 'Lung Cancer',
    stage: 'Stage IV',
    biomarkers: [],
    age: 45,
    sex: 'ALL',
    ecog_score: 0,
    state_location: 'Massachusetts',
    travel_radius_km: 500, // Displayed as Miles, sent as model variable
    has_active_brain_mets: false,
    prior_therapies: [],
  });

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') === 'saved' ? 'saved' : 'recommended';
  });
  const [savedList, setSavedList] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  // Save Bookmarks Modal States
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [activeTrialForBookmark, setActiveTrialForBookmark] = useState(null);
  const [bookmarkNotes, setBookmarkNotes] = useState('');

  // Brief Modal States
  const [activeTrialForBrief, setActiveTrialForBrief] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [briefMarkdown, setBriefMarkdown] = useState('');
  const [copiedBrief, setCopiedBrief] = useState(false);

  const KNOWN_BIOMARKERS = ['EGFR', 'ALK', 'ROS1', 'KRAS', 'HER2', 'BRCA', 'PD-L1', 'BRCA1', 'BRCA2', 'TP53'];
  const PRIOR_THERAPIES = ['Chemotherapy', 'Radiotherapy', 'Immunotherapy', 'Targeted Therapy', 'Surgery'];
  
  // US States for localized US matching
  const US_STATES = [
    'Massachusetts', 'California', 'Texas', 'New York', 'Florida', 'Washington',
    'Pennsylvania', 'Illinois', 'Georgia', 'North Carolina', 'Ohio', 'Maryland', 'Colorado'
  ];

  useEffect(() => {
    fetchProfileAndMatches();
  }, []);

  const fetchSavedTrials = async () => {
    try {
      setLoadingSaved(true);
      setErr('');
      const data = await memberApi.getSavedTrials();
      setSavedList(data);
    } catch (e) {
      setErr(friendlyMemberError(e));
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleUnsave = async (trialId) => {
    try {
      setErr('');
      setMsg('');
      await memberApi.unsaveTrialMatch(trialId);
      setSavedList(prev => prev.filter(t => t.trial_id !== trialId));
      setMsg(`Successfully removed trial bookmark.`);
    } catch (e) {
      setErr(friendlyMemberError(e));
    }
  };

  useEffect(() => {
    if (activeTab === 'saved') {
      fetchSavedTrials();
    }
  }, [activeTab]);

  const mapRecommendedMatches = (recommendedList) => {
    return (recommendedList || []).map(r => {
      // Formulate complete structured evaluations array from criteria_evaluation map
      const evaluations = Object.entries(r.criteria_evaluation || {}).map(([key, val]) => {
        let fieldName = key.replace(/_/g, ' ');
        if (key === 'gender') fieldName = 'Biological Sex';
        if (key === 'ecog_score') fieldName = 'ECOG Performance';
        if (key === 'brain_mets') fieldName = 'Brain Metastases Exclusion';
        if (key === 'prior_treatments' || key === 'prior_therapies') fieldName = 'Prior Treatment Exclusions';

        let notes = 'Meets requirement';
        if (val === 'UNKNOWN') {
          notes = 'Requires clinical oncologist verification';
        } else if (val === 'NOT_MET') {
          notes = 'Does not align with study parameters';
        } else {
          // Add custom helpful notes depending on field
          if (key === 'cancer_type') notes = 'Patient tumor histology matches trial target condition';
          if (key === 'stage') notes = 'Tumor staging is compatible with study protocol';
          if (key === 'age') notes = 'Patient age is within the required study boundaries';
          if (key === 'gender') notes = 'Gender match is verified and cleared';
          if (key === 'ecog_score') notes = 'ECOG functional status cleared';
        }

        return {
          field: fieldName,
          status: val,
          notes: notes
        };
      });

      return {
        ...r,
        score: r.match_percentage !== undefined ? r.match_percentage : 100,
        geographic_fit_score: r.location_score !== undefined ? r.location_score : 100,
        biomarker_align_score: r.biomarker_score !== undefined ? r.biomarker_score : 100,
        clinical_fit_score: r.clinical_score !== undefined ? r.clinical_score : 100,
        evaluations: evaluations
      };
    });
  };

  const fetchProfileAndMatches = async () => {
    try {
      setLoadingProfile(true);
      setLoadingMatches(true);
      setErr('');

      // Fetch matching profile details
      const userProfile = await memberApi.getTrialProfile();
      // Safe guard state_location mapping to US states
      if (userProfile && (!userProfile.state_location || !US_STATES.includes(userProfile.state_location))) {
        userProfile.state_location = 'Massachusetts';
      }
      setProfile(userProfile);
      setLoadingProfile(false);

      // Fetch trial recommendations matches
      const recommended = await memberApi.getTrialMatches();
      setMatches(mapRecommendedMatches(recommended));
    } catch (e) {
      setErr(friendlyMemberError(e));
    } finally {
      setLoadingProfile(false);
      setLoadingMatches(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      setErr('');
      setMsg('');
      const updated = await memberApi.updateTrialProfile(profile);
      setProfile(updated);
      
      // Re-trigger matches refresh
      setLoadingMatches(true);
      const recommended = await memberApi.getTrialMatches();
      setMatches(mapRecommendedMatches(recommended));
      setMsg('Matching parameters saved successfully. Recommendations refreshed!');
    } catch (err) {
      setErr(friendlyMemberError(err));
    } finally {
      setSavingProfile(false);
      setLoadingMatches(false);
    }
  };

  const handleToggleBiomarker = (marker) => {
    const formatted = `${marker}+`;
    setProfile(prev => {
      const biomarkers = prev.biomarkers || [];
      const updated = biomarkers.includes(formatted)
        ? biomarkers.filter(b => b !== formatted)
        : [...biomarkers, formatted];
      return { ...prev, biomarkers: updated };
    });
  };

  const handleToggleTherapy = (therapy) => {
    setProfile(prev => {
      const therapies = prev.prior_therapies || [];
      const updated = therapies.includes(therapy)
        ? therapies.filter(t => t !== therapy)
        : [...therapies, therapy];
      return { ...prev, prior_therapies: updated };
    });
  };

  // Bookmark Trial Flow
  const openBookmarkModal = (trial) => {
    setActiveTrialForBookmark(trial);
    setBookmarkNotes('');
  };

  const handleSaveBookmark = async () => {
    if (!activeTrialForBookmark) return;
    try {
      setSavingBookmark(true);
      await memberApi.saveTrialMatch({
        trial_id: activeTrialForBookmark.id,
        notes: bookmarkNotes
      });
      setActiveTrialForBookmark(null);
      setMsg(`Successfully saved trial ${activeTrialForBookmark.id} to bookmarks.`);
    } catch (e) {
      setErr(friendlyMemberError(e));
    } finally {
      setSavingBookmark(false);
    }
  };

  // Brief View Flow
  const openBriefModal = async (trialId) => {
    try {
      setActiveTrialForBrief(trialId);
      setLoadingBrief(true);
      setCopiedBrief(false);
      const data = await memberApi.getTrialBrief(trialId);
      setBriefMarkdown(data.markdown_brief);
    } catch (e) {
      setErr(friendlyMemberError(e));
      setActiveTrialForBrief(null);
    } finally {
      setLoadingBrief(false);
    }
  };

  const handleCopyBrief = () => {
    navigator.clipboard.writeText(briefMarkdown);
    setCopiedBrief(true);
    setTimeout(() => setCopiedBrief(false), 3000);
  };

  const handlePrintBrief = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Clinical Trial Oncologist Discussion Brief</title>
          <style>
            body { font-family: -apple-system, system-ui, sans-serif; line-height: 1.6; padding: 40px; color: #161616; background-color: #f6f4ef; }
            h1 { color: #0f3e17; border-bottom: 1.5px solid #e6e2d8; padding-bottom: 12px; font-family: serif; font-size: 26px; }
            h2 { color: #0f3e17; margin-top: 24px; font-family: serif; font-size: 20px; }
            pre { background: #ffffff; padding: 20px; border: 1px solid #e6e2d8; border-radius: 12px; white-space: pre-wrap; font-family: inherit; font-size: 13px; color: #161616; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
            hr { border: 0; border-top: 1px solid #e6e2d8; margin: 24px 0; }
          </style>
        </head>
        <body>
          <div style="max-width: 750px; margin: 0 auto;">
            ${briefMarkdown
              .replace(/# (.*)/g, '<h1>$1</h1>')
              .replace(/## (.*)/g, '<h2>$1</h2>')
              .replace(/### (.*)/g, '<h3>$1</h3>')
              .replace(/\*\s(.*)/g, '<li>$1</li>')
              .replace(/\n\n/g, '<br/>')
              .replace(/---/g, '<hr/>')
            }
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getMatchScoreBadgeColor = (percentage) => {
    if (percentage >= 85) return 'bg-[#2f6f3e]/10 text-[#2f6f3e] border-[#2f6f3e]/20';
    if (percentage >= 50) return 'bg-[#c9a227]/10 text-[#c9a227] border-[#c9a227]/20';
    return 'bg-[#b42318]/10 text-[#b42318] border-[#b42318]/20';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 text-[#161616] font-sans">
      {/* Title Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-[#e6e2d8] pb-6 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-[#161616] tracking-tight font-serif">
            Clinical Trial Matching Registry
          </h1>
          <p className="text-sm text-[#6d6a63] mt-2 leading-relaxed max-w-xl">
            Personalized, case-aware search linking oncology patients to verified US clinical trials and international research protocols.
          </p>
        </div>
      </div>

      {err && (
        <div className="p-4 bg-[#b42318]/10 border-l-4 border-[#b42318] text-[#b42318] text-xs font-bold rounded-r-xl mb-6">
          {err}
        </div>
      )}

      {msg && (
        <div className="p-4 bg-[#2f6f3e]/10 border-l-4 border-[#2f6f3e] text-[#2f6f3e] text-xs font-bold rounded-r-xl mb-6">
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Match Profile Editor */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-sm p-6 space-y-6">
            <h2 className="text-sm font-extrabold text-[#161616] border-b border-[#e6e2d8] pb-3 uppercase tracking-wider">
              Matching Parameters
            </h2>

            {loadingProfile ? (
              <div className="flex justify-center py-12">
                <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17]"></span>
              </div>
            ) : (
              <form onSubmit={handleUpdateProfile} className="space-y-5 text-xs font-semibold text-[#161616]">
                {/* Cancer Type & Stage */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      Cancer Type
                    </label>
                    <input
                      type="text"
                      className="w-full text-xs border border-[#e6e2d8] rounded-xl px-3 py-2.5 bg-[#fdfdfc] font-semibold focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all"
                      value={profile.cancer_type}
                      onChange={(e) => setProfile({ ...profile, cancer_type: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      Stage
                    </label>
                    <select
                      className="w-full text-xs border border-[#e6e2d8] rounded-xl px-3 py-2.5 bg-white font-semibold focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all"
                      value={profile.stage}
                      onChange={(e) => setProfile({ ...profile, stage: e.target.value })}
                    >
                      <option value="Stage I">Stage I</option>
                      <option value="Stage II">Stage II</option>
                      <option value="Stage III">Stage III</option>
                      <option value="Stage IV">Stage IV</option>
                    </select>
                  </div>
                </div>

                {/* Age & Sex */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      Age
                    </label>
                    <input
                      type="number"
                      className="w-full text-xs border border-[#e6e2d8] rounded-xl px-3 py-2.5 focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all font-semibold"
                      value={profile.age}
                      onChange={(e) => setProfile({ ...profile, age: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="120"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      Gender
                    </label>
                    <select
                      className="w-full text-xs border border-[#e6e2d8] rounded-xl px-3 py-2.5 bg-white font-semibold focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all"
                      value={profile.sex}
                      onChange={(e) => setProfile({ ...profile, sex: e.target.value })}
                    >
                      <option value="ALL">All Genders</option>
                      <option value="MALE">Male Only</option>
                      <option value="FEMALE">Female Only</option>
                    </select>
                  </div>
                </div>

                {/* State Location & Proximity */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      Primary US State Location
                    </label>
                    <select
                      className="w-full text-xs border border-[#e6e2d8] rounded-xl px-3 py-2.5 bg-white font-semibold focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all"
                      value={profile.state_location}
                      onChange={(e) => setProfile({ ...profile, state_location: e.target.value })}
                    >
                      {US_STATES.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-2">
                      <span>Travel Radius Limit</span>
                      <span className="text-[#0f3e17] font-bold">{profile.travel_radius_km} Miles</span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="1500"
                      step="50"
                      className="w-full accent-[#0f3e17] cursor-pointer"
                      value={profile.travel_radius_km}
                      onChange={(e) => setProfile({ ...profile, travel_radius_km: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                {/* ECOG Score Details */}
                <div className="space-y-2">
                  <div className="flex justify-between text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-1.5">
                    <span>ECOG Performance Status</span>
                    <span className="text-[#0f3e17] font-bold">ECOG {profile.ecog_score}</span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {[0, 1, 2, 3, 4].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setProfile({ ...profile, ecog_score: num })}
                        className={`py-2 text-xs font-bold border rounded-xl transition-all ${
                          profile.ecog_score === num
                            ? 'bg-[#0f3e17] border-[#0f3e17] text-white shadow-sm'
                            : 'bg-white border-[#e6e2d8] text-[#161616] hover:bg-[#ece8df]/20'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#6d6a63] italic leading-normal font-normal">
                    {profile.ecog_score === 0 && '0: Fully active, symmetric pre-disease function.'}
                    {profile.ecog_score === 1 && '1: Restricted in physically strenuous activity.'}
                    {profile.ecog_score === 2 && '2: Ambulatory and capable of all selfcare, up to 50% awake.'}
                    {profile.ecog_score === 3 && '3: Capable of only limited selfcare, confined to bed/chair >50%.'}
                    {profile.ecog_score === 4 && '4: Completely disabled. Cannot carry on any selfcare.'}
                  </p>
                </div>

                {/* Brain Metastasis Check */}
                <div className="flex items-center justify-between p-4 bg-[#ece8df]/10 rounded-xl border border-[#e6e2d8]">
                  <div className="pr-4 space-y-0.5">
                    <span className="block text-xs font-extrabold text-[#161616]">Active Brain Metastasis</span>
                    <span className="text-[10px] text-[#6d6a63] font-normal leading-normal block">Exclude trials banning active CNS metastases.</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={profile.has_active_brain_mets}
                      onChange={(e) => setProfile({ ...profile, has_active_brain_mets: e.target.checked })}
                    />
                    <div className="w-10 h-5.5 bg-[#ece8df] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-[#e6e2d8] after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all peer-checked:bg-[#0f3e17]"></div>
                  </label>
                </div>

                {/* Biomarkers */}
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                    Confirmed Biomarkers
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {KNOWN_BIOMARKERS.map(marker => {
                      const formatted = `${marker}+`;
                      const active = (profile.biomarkers || []).includes(formatted);
                      return (
                        <button
                          key={marker}
                          type="button"
                          onClick={() => handleToggleBiomarker(marker)}
                          className={`px-2.5 py-1.5 text-xs font-bold border rounded-xl transition-all ${
                            active
                              ? 'bg-[#0f3e17]/10 border-[#0f3e17]/30 text-[#0f3e17]'
                              : 'bg-white border-[#e6e2d8] text-[#6d6a63] hover:bg-[#ece8df]/20'
                          }`}
                        >
                          {marker}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Prior Therapies */}
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                    Prior Therapies Passed
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {PRIOR_THERAPIES.map(therapy => {
                      const active = (profile.prior_therapies || []).includes(therapy);
                      return (
                        <button
                          key={therapy}
                          type="button"
                          onClick={() => handleToggleTherapy(therapy)}
                          className={`px-2.5 py-1.5 text-xs font-bold border rounded-xl transition-all ${
                            active
                              ? 'bg-[#0f3e17]/10 border-[#0f3e17]/30 text-[#0f3e17]'
                              : 'bg-white border-[#e6e2d8] text-[#6d6a63] hover:bg-[#ece8df]/20'
                          }`}
                        >
                          {therapy}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="w-full py-3 bg-[#0f3e17] text-white rounded-xl text-xs font-bold hover:bg-[#1a4a21] transition shadow-sm disabled:opacity-50 border border-[#0f3e17] uppercase tracking-wider"
                >
                  {savingProfile ? 'Refreshing Recommendations...' : 'Save Parameters & Match'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Right Column: Recommendations Results List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex border-b border-[#e6e2d8] gap-6 text-xs font-extrabold uppercase tracking-wider mb-2">
            <button
              type="button"
              className={`pb-3 border-b-2 transition-all ${
                activeTab === 'recommended'
                  ? 'border-[#0f3e17] text-[#0f3e17] font-extrabold'
                  : 'border-transparent text-[#6d6a63] hover:text-[#161616]'
              }`}
              onClick={() => setActiveTab('recommended')}
            >
              Recommended Matches ({matches.length})
            </button>
            <button
              type="button"
              className={`pb-3 border-b-2 transition-all ${
                activeTab === 'saved'
                  ? 'border-[#0f3e17] text-[#0f3e17] font-extrabold'
                  : 'border-transparent text-[#6d6a63] hover:text-[#161616]'
              }`}
              onClick={() => setActiveTab('saved')}
            >
              Saved Trials Catalog ({savedList.length})
            </button>
          </div>

          {activeTab === 'recommended' && (
            <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-sm p-6">
              <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-4 mb-6">
                <h2 className="text-sm font-extrabold text-[#161616] uppercase tracking-wider">
                  Recommended US Research Studies
                </h2>
                <span className="px-3 py-1 bg-[#2f6f3e]/10 border border-[#2f6f3e]/20 text-[#2f6f3e] text-[10px] font-bold tracking-wider uppercase rounded-full">
                  {matches.length} Recruiting matches
                </span>
              </div>

            {loadingMatches ? (
              <div className="flex flex-col items-center justify-center py-24 space-y-3">
                <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f3e17]"></span>
                <span className="text-xs text-[#6d6a63] font-bold uppercase tracking-wider">Scoring and screening trials...</span>
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-16 text-[#6d6a63] space-y-3">
                <p className="text-sm italic">No matching trials found in the clinical registry directory matching your criteria.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {matches.map((match) => (
                  <div key={match.trial.id} className="border border-[#e6e2d8] p-5 rounded-2xl bg-[#fdfdfc] hover:border-[#0f3e17]/30 transition-all shadow-sm flex flex-col justify-between">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold font-mono text-[#0f3e17] bg-[#0f3e17]/5 border border-[#0f3e17]/10 px-2 py-0.5 rounded">
                            {match.trial.id}
                          </span>
                          <span className="text-[10px] uppercase font-bold text-[#6d6a63] bg-[#ece8df]/40 px-2 py-0.5 rounded border border-[#e6e2d8]">
                            {match.trial.source || 'clinicaltrials.gov'}
                          </span>
                        </div>
                        <div className={`px-2.5 py-1 text-xs font-bold rounded-lg border flex items-center gap-1 ${getMatchScoreBadgeColor(match.score)}`}>
                          <span>{match.score}% MATCH</span>
                        </div>
                      </div>

                      <h3 className="font-extrabold text-[#161616] text-base leading-snug font-serif">
                        {match.trial.title}
                      </h3>
                      <p className="text-xs text-[#6d6a63] font-medium leading-relaxed italic">
                        "{match.trial.brief_summary}"
                      </p>

                      <div className="grid grid-cols-3 gap-2 py-2 text-center text-xs font-semibold text-[#161616]">
                        <div className="p-3 bg-[#ece8df]/20 border border-[#e6e2d8]/60 rounded-xl space-y-1">
                          <div className="text-[9px] font-extrabold text-[#6d6a63] uppercase tracking-wide">LOCATION SCORE</div>
                          <span className="text-xs font-bold text-[#161616] flex items-center justify-center gap-1">
                            <SvgLocationPin />
                            {match.geographic_fit_score}%
                          </span>
                        </div>
                        <div className="p-3 bg-[#ece8df]/20 border border-[#e6e2d8]/60 rounded-xl space-y-1">
                          <div className="text-[9px] font-extrabold text-[#6d6a63] uppercase tracking-wide">BIOMARKER ALIGN</div>
                          <span className="text-xs font-bold text-[#161616] flex items-center justify-center gap-1">
                            <SvgCompass />
                            {match.biomarker_align_score}%
                          </span>
                        </div>
                        <div className="p-3 bg-[#ece8df]/20 border border-[#e6e2d8]/60 rounded-xl space-y-1">
                          <div className="text-[9px] font-extrabold text-[#6d6a63] uppercase tracking-wide">CLINICAL FIT</div>
                          <span className="text-xs font-bold text-[#161616] flex items-center justify-center gap-1">
                            <SvgCompass />
                            {match.clinical_fit_score}%
                          </span>
                        </div>
                      </div>

                      <div className="bg-[#f6f4ef]/40 border border-[#e6e2d8] p-4 rounded-xl space-y-3.5 text-xs text-[#161616] font-semibold">
                        <div>
                          <span className="text-[10px] font-extrabold text-[#2f6f3e] uppercase tracking-wider block mb-1">✓ Met Eligibility Criteria</span>
                          <ul className="list-inside list-disc text-[11px] text-[#6d6a63] space-y-1 font-medium leading-relaxed">
                            {match.evaluations?.filter(e => e.status === 'MET').map((e, idx) => (
                              <li key={idx}>
                                <strong className="text-[#161616] capitalize">{e.field.replace(/_/g, ' ')}</strong>: {e.notes}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {match.evaluations?.some(e => e.status === 'UNKNOWN') && (
                          <div className="border-t border-[#e6e2d8] pt-2">
                            <span className="text-[10px] font-extrabold text-[#c9a227] uppercase tracking-wider block mb-1">⚠️ Discuss with Medical Oncologist (Requires Verification)</span>
                            <ul className="list-inside list-disc text-[11px] text-[#6d6a63] space-y-1 font-medium leading-relaxed">
                              {match.evaluations?.filter(e => e.status === 'UNKNOWN').map((e, idx) => (
                                <li key={idx}>
                                  <strong className="text-[#161616] capitalize">{e.field.replace(/_/g, ' ')}</strong> is unknown or unverified: {e.notes}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3 justify-end pt-5 border-t border-[#e6e2d8]/60 mt-5">
                      <button 
                        onClick={() => openBookmarkModal(match.trial)} 
                        className="py-2.5 px-4 bg-white border border-[#e6e2d8] text-[#161616] hover:border-[#0f3e17] text-xs font-bold rounded-xl transition flex items-center gap-1.5"
                      >
                        <SvgBookmark />
                        <span>Save Bookmark</span>
                      </button>
                      <button 
                        onClick={() => openBriefModal(match.trial.id)} 
                        className="py-2.5 px-4.5 bg-[#0f3e17] hover:bg-[#194a22] text-white text-xs font-bold rounded-xl shadow-sm transition flex items-center gap-1.5 border border-[#0f3e17]"
                      >
                        <SvgBriefcase />
                        <span>Oncologist Brief</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {activeTab === 'saved' && (
            <div className="space-y-6">
              {loadingSaved ? (
                <div className="flex flex-col items-center justify-center py-24 space-y-3">
                  <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17]"></span>
                  <span className="text-xs text-[#6d6a63] font-bold uppercase tracking-wider">Fetching saved trials...</span>
                </div>
              ) : savedList.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-sm p-16 text-center space-y-4 max-w-xl mx-auto">
                  <div className="flex justify-center">
                    <SvgLocationPin />
                  </div>
                  <h2 className="text-lg font-extrabold text-[#161616] font-serif">Your saved catalog is empty</h2>
                  <p className="text-xs text-[#6d6a63] font-semibold max-w-md mx-auto leading-relaxed">
                    Bookmarked oncology trials from the recommendations tab will show up here, along with your treatment queries.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {savedList.map(item => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl border border-[#e6e2d8] shadow-sm p-6 hover:border-[#0f3e17]/30 transition-all flex flex-col justify-between space-y-4"
                    >
                      <div className="space-y-3.5">
                        {/* NCT ID and source */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold font-mono text-[#0f3e17] bg-[#0f3e17]/5 border border-[#0f3e17]/10 px-2 py-0.5 rounded">
                            {item.trial_id}
                          </span>
                          <span className="text-[9px] uppercase font-extrabold text-[#6d6a63] bg-[#ece8df]/40 px-2.5 py-0.5 rounded-full border border-[#e6e2d8] tracking-wider">
                            {item.trial_source || 'clinicaltrials.gov'}
                          </span>
                        </div>

                        {/* Study Title */}
                        <h3 className="text-sm font-extrabold text-[#161616] leading-snug line-clamp-3 font-serif">
                          {item.trial_title}
                        </h3>

                        {/* Personal Notes */}
                        {item.notes && (
                          <div className="p-4 bg-[#ece8df]/15 border border-[#e6e2d8] rounded-xl space-y-1.5">
                            <span className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                              My Clinical Discussion Notes:
                            </span>
                            <p className="text-xs text-[#161616] leading-relaxed italic font-medium">
                              "{item.notes}"
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 justify-end border-t border-[#e6e2d8]/60 pt-4 mt-2">
                        <button
                          onClick={() => handleUnsave(item.trial_id)}
                          className="py-2 px-3 bg-white border border-red-200 text-[#b42318] hover:bg-red-50 text-xs font-extrabold rounded-xl transition flex items-center gap-1"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => openBriefModal(item.trial_id)}
                          className="py-2 px-3 bg-[#0f3e17] text-white hover:bg-[#194a22] text-xs font-bold rounded-xl transition flex items-center gap-1"
                        >
                          Oncologist Brief
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 1. SAVE BOOKMARK MODAL */}
      {activeTrialForBookmark && (
        <div className="fixed inset-0 bg-[#161616]/65 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full border border-[#e6e2d8] shadow-2xl p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-3 text-xs font-semibold">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif">Save Study Bookmark</h3>
                <p className="text-xs text-[#6d6a63] mt-0.5">Study reference: <span className="font-mono text-[#0f3e17] font-bold">{activeTrialForBookmark.id}</span></p>
              </div>
              <button onClick={() => setActiveTrialForBookmark(null)} className="p-1.5 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition">
                <SvgClose />
              </button>
            </div>

            <div className="space-y-1.5 text-xs font-semibold">
              <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                My Clinical Discussion Notes
              </label>
              <textarea
                className="w-full text-xs border border-[#e6e2d8] rounded-xl p-3 bg-white font-semibold focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all h-28 leading-relaxed"
                placeholder="List questions about eligibility, required blood tests, staging criteria, or travel support to ask your clinician..."
                value={bookmarkNotes}
                onChange={(e) => setBookmarkNotes(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-[#e6e2d8]">
              <button
                onClick={() => setActiveTrialForBookmark(null)}
                className="py-2 px-4 border border-[#e6e2d8] hover:bg-[#ece8df]/40 text-xs font-bold text-[#6d6a63] rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBookmark}
                disabled={savingBookmark}
                className="py-2.5 px-5 bg-[#0f3e17] text-white hover:bg-[#1a4a21] text-xs font-bold rounded-xl transition shadow-sm disabled:opacity-50 border border-[#0f3e17]"
              >
                {savingBookmark ? 'Saving Bookmarks...' : 'Save Catalog Bookmark'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. ONCOLOGIST BRIEF MODAL */}
      {activeTrialForBrief && (
        <div className="fixed inset-0 bg-[#161616]/65 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-2xl w-full border border-[#e6e2d8] shadow-2xl p-6 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-4 flex-shrink-0">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif">Oncologist Discussion Brief</h3>
                <p className="text-xs text-[#6d6a63] mt-0.5">Automated clinical fit checklist for your consultation discussion.</p>
              </div>
              <button onClick={() => setActiveTrialForBrief(null)} className="p-1.5 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition">
                <SvgClose />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-5 pr-2 space-y-4">
              {loadingBrief ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-2">
                  <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17]"></span>
                  <span className="text-xs text-[#6d6a63] font-bold uppercase tracking-wider">Generating brief...</span>
                </div>
              ) : (
                <div className="bg-[#ece8df]/25 border border-[#e6e2d8] rounded-2xl p-5 shadow-inner">
                  <pre className="text-xs font-mono text-[#161616] whitespace-pre-wrap leading-relaxed">
                    {briefMarkdown}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-between gap-3 border-t border-[#e6e2d8] pt-4 flex-shrink-0">
              <button
                onClick={handlePrintBrief}
                disabled={loadingBrief}
                className="py-2.5 px-4 bg-white border border-[#e6e2d8] hover:border-[#0f3e17] text-xs font-bold rounded-xl transition flex items-center gap-1.5"
              >
                <span>Print Brief Document</span>
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyBrief}
                  disabled={loadingBrief}
                  className="py-2.5 px-4 bg-white border border-[#e6e2d8] hover:border-[#0f3e17] text-xs font-bold rounded-xl transition flex items-center gap-1.5"
                >
                  <span>{copiedBrief ? 'Copied to Clipboard!' : 'Copy to Clipboard'}</span>
                </button>
                <button
                  onClick={() => setActiveTrialForBrief(null)}
                  className="py-2.5 px-5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white text-xs font-bold rounded-xl transition shadow-sm border border-[#0f3e17]"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
